import React, { useEffect, useState, useRef } from 'react';
import Text from '../components/elements/TextComponent';
import InputField from '../components/elements/InputField';
import TextWithButton from '../components/elements/TextWithButtonComponent';
import Button from '../components/elements/Button';
import ImageComponent from '../components/elements/ImageComponent';
import userAtDesk from '../assets/userAtDesk.svg';
import { sendUpdatedPasswordReset } from '../api/userApi';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PURPLE_HEIGHT = 240;

const MobileReset: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [imageSize, setImageSize] = useState(160);
  const whiteSectionRef = useRef<HTMLDivElement>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const emailParam = searchParams.get('email');
    if (tokenParam && emailParam) {
      setToken(tokenParam);
      setEmail(emailParam);
    } else {
      setMessage('Invalid password reset link.');
    }
  }, [searchParams]);

  useEffect(() => {
    const update = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      const keyboardHeight = window.innerHeight - vh;
      setKeyboardOpen(keyboardHeight > 100);
      const w = window.innerWidth;
      const size = Math.min(w * 0.45, vh * 0.3);
      setImageSize(Math.max(140, Math.min(size, 220)));
    };
    update();
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const handleSubmit = async () => {
    try {
      const res = await sendUpdatedPasswordReset(email, token, newPassword);
      if (res.success) {
        setStatus('success');
        setMessage('Your password has been reset!');
      } else {
        setStatus('error');
        setMessage(res.msg || 'Failed to send reset email.');
      }
    } catch (error: any) {
      setStatus('error');
      setMessage(error.response?.data?.message || 'Error resetting password.');
    }
  };

  return (
    <div
      style={{
        height: '100vh',
        fontFamily: 'Montserrat',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Purple Section */}
      <div
        style={{
          height: PURPLE_HEIGHT,
          backgroundColor: 'var(--primary)',
          padding: '24px 16px',
          display: keyboardOpen ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <ImageComponent
          src={userAtDesk}
          center
          style={{ width: `${imageSize}px`, height: `${imageSize}px`, objectFit: 'contain' }}
        />
        <div>
          <Text
            text="ComeChatWithMe.ai"
            fontSize="24px"
            fontWeight="700"
            color="#ffffff"
            textAlign="center"
            navigateToHome
          />
          <Text
            text="Reset your account password"
            fontSize="14px"
            fontWeight="400"
            color="#ffffff"
            textAlign="center"
          />
        </div>
      </div>

      {/* White Scrollable Section */}
      <div
        ref={whiteSectionRef}
        style={{
          flex: 1,
          backgroundColor: '#ffffff',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          overflowY: 'auto',
          padding: '24px 20px 160px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Text text="Reset Password" fontSize="24px" fontWeight="700" color="#030303" textAlign="left" />

        <div style={{ marginTop: 12 }}>
          <TextWithButton
            text="Back to "
            fontSize="14px"
            fontWeight="400"
            color="#030303"
            textAlign="left"
            showButton
            buttonLabel="login"
            onButtonClick={() => navigate('/login')}
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <Text text="New Password" fontSize="16px" fontWeight="400" color="#030303" textAlign="left" />
        </div>

        <InputField
          text=""
          fontSize="16px"
          fontWeight={500}
          type="password"
          onChange={(e) => setNewPassword(e.target.value)}
        />

        {status !== 'idle' && (
          <p
            style={{
              fontFamily: 'Montserrat',
              fontSize: '14px',
              color: status === 'success' ? '#2e7d32' : '#d32f2f',
              textAlign: 'left',
              marginTop: 10,
            }}
          >
            {message}
          </p>
        )}

        <div style={{ marginTop: 24 }}>
          <Button
            label="Reset Password"
            backgroundColor="var(--primary)"
            color="#ffffff"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default MobileReset;
